import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { 
  LayoutDashboard, 
  Newspaper, 
  BookOpen, 
  Users, 
  LogOut,
  Plus,
  Pencil,
  Trash2,
  Shield,
  ShieldCheck,
  Video
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminUsers = () => {
  const { admin, token, logout } = useAuth();
  const navigate = useNavigate();
  const [adminList, setAdminList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [newAdmin, setNewAdmin] = useState({
    email: '',
    password: '',
    full_name: '',
    is_super_admin: false
  });

  useEffect(() => {
    fetchAdmins();
  }, []);

  const fetchAdmins = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/list`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setAdminList(await response.json());
      } else if (response.status === 403) {
        toast.error('Accès réservé aux super administrateurs');
        navigate('/admin');
      }
    } catch (error) {
      console.error('Error fetching admins:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleAddAdmin = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_URL}/api/admin/register`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newAdmin)
      });
      
      if (response.ok) {
        toast.success('Administrateur ajouté');
        setShowAddModal(false);
        setNewAdmin({ email: '', password: '', full_name: '', is_super_admin: false });
        fetchAdmins();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de l\'ajout');
      }
    } catch (error) {
      toast.error('Erreur lors de l\'ajout');
    }
  };

  const handleDelete = async (id) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setAdminList(adminList.filter(a => a.id !== id));
        toast.success('Administrateur supprimé');
        setDeleteId(null);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de la suppression');
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const toggleSuperAdmin = async (adminUser) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/${adminUser.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ is_super_admin: !adminUser.is_super_admin })
      });
      if (response.ok) {
        const updated = await response.json();
        setAdminList(adminList.map(a => a.id === adminUser.id ? updated : a));
        toast.success('Droits modifiés');
      }
    } catch (error) {
      toast.error('Erreur lors de la modification');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const menuItems = [
    { icon: LayoutDashboard, label: 'Tableau de bord', path: '/admin' },
    { icon: Newspaper, label: 'Actualités', path: '/admin/news' },
    { icon: BookOpen, label: 'Livres', path: '/admin/books' },
    { icon: Video, label: 'Interviews', path: '/admin/videos' },
    { icon: Users, label: 'Utilisateurs', path: '/admin/users', active: true }
  ];

  return (
    <div className="min-h-screen bg-stone-100 flex">
      {/* Sidebar */}
      <aside className="w-64 bg-stone-900 text-white flex flex-col">
        <div className="p-6 border-b border-stone-700">
          <h1 className="text-xl font-serif font-bold">Quid Novi</h1>
          <p className="text-stone-400 text-sm mt-1">Administration</p>
        </div>

        <nav className="flex-1 p-4">
          <ul className="space-y-2">
            {menuItems.map((item) => (
              <li key={item.path}>
                <Link
                  to={item.path}
                  className={`flex items-center gap-3 px-4 py-3 rounded transition-colors ${
                    item.active 
                      ? 'bg-hermes-600 text-white' 
                      : 'text-stone-300 hover:bg-stone-800'
                  }`}
                >
                  <item.icon className="w-5 h-5" />
                  <span>{item.label}</span>
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <div className="p-4 border-t border-stone-700">
          <div className="flex items-center gap-3 px-4 py-2 text-stone-300">
            <Users className="w-5 h-5" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{admin?.full_name || admin?.email}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 mt-2 text-stone-400 hover:text-white hover:bg-stone-800 rounded transition-colors"
          >
            <LogOut className="w-5 h-5" />
            <span>Déconnexion</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 p-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-2xl font-serif font-bold text-stone-900">Gestion des utilisateurs</h2>
            <p className="text-stone-600 mt-1">Gérez les accès à l'administration</p>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-hermes-600 text-white hover:bg-hermes-700 transition-colors"
            data-testid="add-admin-button"
          >
            <Plus className="w-5 h-5" />
            Ajouter un utilisateur
          </button>
        </div>

        {/* Admin List */}
        <div className="bg-white shadow-sm">
          <table className="w-full">
            <thead className="bg-stone-50 border-b border-stone-200">
              <tr>
                <th className="text-left px-6 py-4 text-sm font-medium text-stone-600">Nom</th>
                <th className="text-left px-6 py-4 text-sm font-medium text-stone-600">Email</th>
                <th className="text-left px-6 py-4 text-sm font-medium text-stone-600">Rôle</th>
                <th className="text-left px-6 py-4 text-sm font-medium text-stone-600">Date d'ajout</th>
                <th className="text-right px-6 py-4 text-sm font-medium text-stone-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {loading ? (
                <tr>
                  <td colSpan="5" className="px-6 py-8 text-center text-stone-500">
                    Chargement...
                  </td>
                </tr>
              ) : adminList.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-8 text-center text-stone-500">
                    Aucun utilisateur
                  </td>
                </tr>
              ) : (
                adminList.map((adminUser) => (
                  <tr key={adminUser.id} className="hover:bg-stone-50">
                    <td className="px-6 py-4">
                      <p className="font-medium text-stone-900">{adminUser.full_name || '-'}</p>
                    </td>
                    <td className="px-6 py-4 text-stone-600">{adminUser.email}</td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded ${
                        adminUser.is_super_admin 
                          ? 'bg-hermes-100 text-hermes-700' 
                          : 'bg-stone-100 text-stone-700'
                      }`}>
                        {adminUser.is_super_admin ? (
                          <>
                            <ShieldCheck className="w-3 h-3" />
                            Super Admin
                          </>
                        ) : (
                          <>
                            <Shield className="w-3 h-3" />
                            Éditeur
                          </>
                        )}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-stone-600">
                      {new Date(adminUser.created_at).toLocaleDateString('fr-FR')}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        {adminUser.id !== admin?.id && (
                          <>
                            <button
                              onClick={() => toggleSuperAdmin(adminUser)}
                              className="p-2 text-stone-500 hover:text-hermes-600 transition-colors"
                              title={adminUser.is_super_admin ? 'Rétrograder' : 'Promouvoir'}
                            >
                              {adminUser.is_super_admin ? (
                                <Shield className="w-4 h-4" />
                              ) : (
                                <ShieldCheck className="w-4 h-4" />
                              )}
                            </button>
                            <button
                              onClick={() => setDeleteId(adminUser.id)}
                              className="p-2 text-stone-500 hover:text-red-600 transition-colors"
                              title="Supprimer"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </>
                        )}
                        {adminUser.id === admin?.id && (
                          <span className="text-xs text-stone-400">Vous</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Add Admin Modal */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white p-6 max-w-md w-full mx-4 shadow-xl">
              <h3 className="text-lg font-medium text-stone-900 mb-6">
                Ajouter un utilisateur
              </h3>
              <form onSubmit={handleAddAdmin} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">
                    Nom complet
                  </label>
                  <input
                    type="text"
                    value={newAdmin.full_name}
                    onChange={(e) => setNewAdmin({...newAdmin, full_name: e.target.value})}
                    className="w-full px-3 py-2 border border-stone-300 focus:border-hermes-600 outline-none"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    value={newAdmin.email}
                    onChange={(e) => setNewAdmin({...newAdmin, email: e.target.value})}
                    className="w-full px-3 py-2 border border-stone-300 focus:border-hermes-600 outline-none"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">
                    Mot de passe
                  </label>
                  <input
                    type="password"
                    value={newAdmin.password}
                    onChange={(e) => setNewAdmin({...newAdmin, password: e.target.value})}
                    className="w-full px-3 py-2 border border-stone-300 focus:border-hermes-600 outline-none"
                    required
                  />
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="is_super_admin"
                    checked={newAdmin.is_super_admin}
                    onChange={(e) => setNewAdmin({...newAdmin, is_super_admin: e.target.checked})}
                    className="w-4 h-4"
                  />
                  <label htmlFor="is_super_admin" className="text-sm text-stone-700">
                    Super administrateur (tous les droits)
                  </label>
                </div>
                <div className="flex gap-3 justify-end pt-4">
                  <button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    className="px-4 py-2 text-stone-600 hover:text-stone-900 transition-colors"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-hermes-600 text-white hover:bg-hermes-700 transition-colors"
                  >
                    Ajouter
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {deleteId && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white p-6 max-w-sm w-full mx-4 shadow-xl">
              <h3 className="text-lg font-medium text-stone-900 mb-2">
                Confirmer la suppression
              </h3>
              <p className="text-stone-600 mb-6">
                Êtes-vous sûr de vouloir supprimer cet utilisateur ?
              </p>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setDeleteId(null)}
                  className="px-4 py-2 text-stone-600 hover:text-stone-900 transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={() => handleDelete(deleteId)}
                  className="px-4 py-2 bg-red-600 text-white hover:bg-red-700 transition-colors"
                >
                  Supprimer
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminUsers;
